
import Component from "vue-class-component";
import Vue from "vue";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import { ApprovalStatus, SeafarerProfileLightViewModel, SeafarerProfileSortField, SeafarersClient, SearchSeafarersActions, } from "@/api-client";
import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
import Loader from "@/features/shared/components/Loader";
import { Prop } from "vue-property-decorator";
import { debounceFn } from "debounce-decorator-ts";
import { BvTableCtxObject } from "bootstrap-vue";
import ServersidePaging from "@/components/ServersidePaging";
import FilterCheckbox from "@/components/FilterCheckbox.vue";
import CardQuicklink from "@/features/dashboard/components/CardQuicklink.vue";
import { NavigationGuardNext } from "vue-router/types/router";
import { ls } from "@/CultureService";
import {formatDate} from "@/formatting/dateFormatter";
import ServersidePagingSelector from "@/components/ServersidePagingSelector.vue";

class Filters {
    onlyActive: boolean;
    private _searchPhrase = "";

    constructor(searchPhrase?: string, onlyActive?: boolean) {
        this.searchPhrase = searchPhrase ?? "";
        this.onlyActive = onlyActive ?? true;
    }
    set searchPhrase(searchPhrase: string) {
        this._searchPhrase = searchPhrase.trim();
    }
    get searchPhrase(): string {
        return this._searchPhrase;
    }
}

class Sort {
    value: SeafarerProfileSortField | null = null;

    constructor(value: SeafarerProfileSortField | null = null) {
        this.value = value;
    }

    get fieldName(): string | null {
        switch (this.value) {
            case SeafarerProfileSortField.FirstNameAsc:
            case SeafarerProfileSortField.FirstNameDesc:
                return "firstName";
            case SeafarerProfileSortField.LastNameAsc:
            case SeafarerProfileSortField.LastNameDesc:
                return "lastName";
            case SeafarerProfileSortField.ApprovalAsc:
            case SeafarerProfileSortField.ApprovalDesc:
                return "approval";
            default:
                return null;
        }
    }

    get isSortedByDesc(): boolean {
        return this.value == null
            || this.value == SeafarerProfileSortField.FirstNameDesc
            || this.value == SeafarerProfileSortField.LastNameDesc
            || this.value == SeafarerProfileSortField.ApprovalDesc;
    }
}

interface Props {
    availableActions: SearchSeafarersActions;
}

@Component({
    components: {
        SectionPageHeading,
        HeadingWave,
        TableEmptySlot,
        FilterCheckbox,
        CardQuicklink,
        ServersidePagingSelector
    },
    beforeRouteEnter(to, from, next: NavigationGuardNext<SearchSeafarer>) {
        const props = to.meta!.props as Props;
        Promise.all([
            new SeafarersClient().availableSearchActions()
        ]).then(values => {
            props.availableActions = values[0];
            next();
        }).catch(e => next(e))
    }
})
export default class SearchSeafarer extends Vue implements Props {
    @Prop({ required: true })
    availableActions!: SearchSeafarersActions;

    seafarers: SeafarerProfileLightViewModel[] = [];
    loader = new Loader();

    approvalStatuses = ApprovalStatus;

    filters = new Filters();
    paging = new ServersidePaging();
    sort = new Sort();

    async created() {
        this.getParametersFromRouteOrDefault();
        this.reflectParametersToRoute();

        this.$watch(() => this.filters, () => {
            this.paging.pageNumber = 1;
            this.reflectParametersToRoute();
        }, { deep: true });

        this.$watch(() => this.paging.pageNumber, () => {
            this.reflectParametersToRoute();
        });

        this.$watch(() => this.paging.pageSize, () => {
            this.paging.pageNumber = 1;
            this.reflectParametersToRoute();
        });

        this.$watch(() => this.sort, () => {
            this.reflectParametersToRoute();
        }, { deep: true });

        this.$watch(() => this.$route.query, () => {
            this.search();
        }, { deep: true });
    }

    search() {
        this.seafarers = [];

        if (!this.availableActions.canSearchForAllSeafarers && this.filters.searchPhrase.length == 0) {
            this.paging.reset();
            return;
        }

        new SeafarersClient()
            .setLoader(this.loader)
            .search(
                this.filters.onlyActive,
                this.sort.value,
                this.paging.pageNumber,
                this.paging.pageSize,
                this.filters.searchPhrase)
            .then(
                result => {
                    this.seafarers = result.pageResult;
                    this.paging.totalCount = result.totalCount;
                    this.paging.pageSize = result.pageSize;
                });
    }

    @debounceFn(100) // Prevents possible double-update
    reflectParametersToRoute(): void {
        this.$router.replace({
            query: {
                // Setting to 'undefined' removes parameter from URL.
                searchPhrase: this.filters.searchPhrase,
                onlyActive: String(this.filters.onlyActive),
                pageNumber: this.paging.pageNumber ? this.paging.pageNumber.toString() : undefined,
                pageSize: this.paging.pageSize ? this.paging.pageSize.toString() : undefined,
                sort: this.sort.value || undefined
            }
        });
    }

    getParametersFromRouteOrDefault() {
        const route = this.$route as any;
        const searchPhrase: string | undefined = route.query.searchPhrase;
        const onlyActive: boolean = this.availableActions.canViewInactiveSeafarers
            ? (route.query.onlyActive?.trim().toLowerCase() === 'true') ?? false
            : true;

        this.filters = new Filters(
            searchPhrase,
            onlyActive
        );

        this.paging.pageNumber = parseInt(route.query.pageNumber) || this.paging.pageNumber;
        this.paging.pageSize = parseInt(route.query.pageSize) || this.paging.pageSize;
        this.sort = new Sort(route.query.sort || null);
    }

    get noResultText() {
        if (!this.availableActions.canSearchForAllSeafarers && this.filters.searchPhrase.length == 0) {
            return ls.Seafarers_Search_Placeholder_CanNotViewAllSeafarers;
        }
        else {
            return ls.Seafarers_Placeholder_NoSeafarers;
        }
    }

    updateSort(ctx: BvTableCtxObject) {
        switch (ctx.sortBy) {
            case "firstName":
                this.sort = new Sort(ctx.sortDesc ? SeafarerProfileSortField.FirstNameDesc : SeafarerProfileSortField.FirstNameAsc);
                break;
            case "lastName":
                this.sort = new Sort(ctx.sortDesc ? SeafarerProfileSortField.LastNameDesc : SeafarerProfileSortField.LastNameAsc);
                break;
            case "approval":
                this.sort = new Sort(ctx.sortDesc ? SeafarerProfileSortField.ApprovalDesc : SeafarerProfileSortField.ApprovalAsc);
                break;
            default:
                this.sort = new Sort();
        }
    }

    navigateToSeafarerProfile(seafarer: SeafarerProfileLightViewModel) {
        this.$router.push({ name: "person-profile", params: { seafarerId: seafarer.id } });
    }

    seafarersTableFields = [
        { key: 'firstName', label: this.$ls.Global_Label_FirstName, sortable: true },
        { key: 'lastName', label: this.$ls.Global_Label_LastName, sortable: true },
        { key: 'birthDate', label: this.$ls.Global_Label_DateOfBirth, sortable: false,
            formatter: (value: any, key: any, item: SeafarerProfileLightViewModel) => formatDate(item.birthDate, "-") },
        { key: 'personalCode', label: this.$ls.Global_Label_PersonalCode, sortable: false },
        { key: 'approval', label: this.$ls.Seafarers_Table_Label_PersonalInfoApproval, sortable: true },
        { key: 'hasInfo', label: '', sortable: false, tdClass: 'table-actions' },
        { key: 'linkIcon', label: '', sortable: false, tdClass: 'table-actions' }
    ];
}
